import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/smartApiService";
import { DASHBOARD_URLS } from "../../../api/DashboardUrls";
import { showAlertAutoClose } from "../../../services/notifyService";

interface AlertDetailModalProps {
  alertId: number;
  onClose: () => void;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({
  alertId,
  onClose,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [alertData, setAlertData] = useState<any>(null);

  useEffect(() => {
    loadAlertDetail();
  }, [alertId]);

  const loadAlertDetail = () => {
    setLoading(true);
    get(`${DASHBOARD_URLS.ALERT_DETAIL}${alertId}`).subscribe({
      next: (response: any) => {
        setAlertData(response.data);
        setLoading(false);
      },
      error: (err) => {
        showAlertAutoClose("Failed to load alert details", "error");
        setLoading(false);
        onClose();
      },
    });
  };

  const handleStatusUpdate = (status: string) => {
    post(`${DASHBOARD_URLS.ALERT_UPDATE_STATUS}${alertId}/status`, { status }).subscribe({
      next: () => {
        showAlertAutoClose(`Alert marked as ${status}`, "success");
        onClose();
      },
      error: (err) => {
        showAlertAutoClose("Failed to update alert status", "error");
      },
    });
  };

  const getSignalColor = (signal: string) => {
    switch (signal?.toLowerCase()) {
      case "bullish":
        return "is-success";
      case "bearish":
        return "is-danger";
      default:
        return "is-warning";
    }
  };

  const formatPatternName = (pattern: string) => {
    return pattern
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-card" style={{ width: "900px", maxWidth: "90vw" }}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            <span className="icon">
              <i className="fa fa-bell"></i>
            </span>
            <span>Alert Details</span>
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={onClose}
          ></button>
        </header>

        <section className="modal-card-body">
          {loading ? (
            <div className="has-text-centered p-6">
              <div className="loader is-loading"></div>
              <p>Loading alert details...</p>
            </div>
          ) : alertData ? (
            <>
              {/* Company Information */}
              <div className="box">
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <div>
                        <h3 className="title is-4">{alertData.company?.name}</h3>
                        <p className="subtitle is-6">
                          {alertData.company?.symbol} • {alertData.company?.sector}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <div className="has-text-right">
                        <p className="title is-3">
                          ₹{alertData.company?.current_price?.toFixed(2)}
                        </p>
                        <p
                          className={`subtitle is-6 ${
                            alertData.company?.day_change >= 0
                              ? "has-text-success"
                              : "has-text-danger"
                          }`}
                        >
                          {alertData.company?.day_change >= 0 ? "+" : ""}
                          {alertData.company?.day_change?.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="level is-mobile">
                  <div className="level-item has-text-centered">
                    <div>
                      <p className="heading">Day High</p>
                      <p className="title is-6">
                        ₹{alertData.company?.day_high?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="level-item has-text-centered">
                    <div>
                      <p className="heading">Day Low</p>
                      <p className="title is-6">
                        ₹{alertData.company?.day_low?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="level-item has-text-centered">
                    <div>
                      <p className="heading">Market Cap</p>
                      <p className="title is-6">
                        {alertData.company?.market_cap_category || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alert Information */}
              <div className="box">
                <h4 className="title is-5 mb-3">Alert Information</h4>

                <div className="notification is-light">
                  <p className="is-size-5">{alertData.alert?.alert_message}</p>
                </div>

                <div className="columns">
                  <div className="column">
                    <div className="field">
                      <label className="label">Signal Type</label>
                      <span
                        className={`tag is-large ${getSignalColor(
                          alertData.alert?.signal_type
                        )}`}
                      >
                        {alertData.alert?.signal_type?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="column">
                    <div className="field">
                      <label className="label">Primary Timeframe</label>
                      <span className="tag is-large is-info">
                        {alertData.alert?.primary_timeframe}
                      </span>
                    </div>
                  </div>
                  <div className="column">
                    <div className="field">
                      <label className="label">Overall Score</label>
                      <span className="tag is-large is-warning">
                        {alertData.alert?.overall_score?.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div className="column">
                    <div className="field">
                      <label className="label">Status</label>
                      <span className="tag is-large is-info">
                        {alertData.alert?.status}
                      </span>
                    </div>
                  </div>
                </div>

                {alertData.alert?.aligned_frames?.length > 0 && (
                  <div className="field">
                    <label className="label">Aligned Timeframes</label>
                    <div className="tags">
                      {alertData.alert.aligned_frames.map(
                        (frame: string, idx: number) => (
                          <span key={idx} className="tag is-success is-medium">
                            {frame}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div className="field">
                  <label className="label">Alert Time</label>
                  <p>{new Date(alertData.alert?.timestamp).toLocaleString()}</p>
                </div>
              </div>

              {/* Detected Patterns */}
              {alertData.patterns && alertData.patterns.length > 0 && (
                <div className="box">
                  <h4 className="title is-5 mb-3">Detected Patterns</h4>
                  <div className="table-container">
                    <table className="table is-fullwidth is-striped">
                      <thead>
                        <tr>
                          <th>Pattern</th>
                          <th>Timeframe</th>
                          <th>Confidence</th>
                          <th>Volume Confirmed</th>
                          <th>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertData.patterns.map((pattern: any) => (
                          <tr key={pattern.id}>
                            <td>
                              <span className="tag is-light">
                                {formatPatternName(pattern.pattern_type)}
                              </span>
                            </td>
                            <td>
                              <span className="tag is-info">
                                {pattern.timeframe}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`tag ${
                                  pattern.confidence >= 0.8
                                    ? "is-success"
                                    : pattern.confidence >= 0.6
                                    ? "is-warning"
                                    : "is-danger"
                                }`}
                              >
                                {(pattern.confidence * 100).toFixed(0)}%
                              </span>
                            </td>
                            <td>
                              {pattern.volume_confirmed ? (
                                <span className="tag is-success">Yes</span>
                              ) : (
                                <span className="tag is-light">No</span>
                              )}
                            </td>
                            <td>
                              {new Date(pattern.timestamp).toLocaleTimeString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="notification is-warning">
              <p>No alert data available</p>
            </div>
          )}
        </section>

        <footer className="modal-card-foot">
          <div className="buttons">
            <button
              className="button is-success"
              onClick={() => handleStatusUpdate("sent")}
            >
              Mark as Sent
            </button>
            <button
              className="button is-light"
              onClick={() => handleStatusUpdate("dismissed")}
            >
              Dismiss
            </button>
            <button className="button" onClick={onClose}>
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AlertDetailModal;
